import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import { LoadingButton } from '@mui/lab';
import {
    Box,
    Card,
    CardContent,
    Chip,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import { Confirm, Report } from 'notiflix';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { createDelivery } from '../../../data/api/moveDeliveries/MoveDeliveryRoutes';
import useIndex from '../../../hooks/useIndex';
import validErrors from '../../../hooks/validErrors';
import DeliveryQrModal from './DeliveryQrModal';

const fieldStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        background: '#fff',
        '& fieldset': { borderColor: 'rgba(15, 34, 77, 0.14)' },
        '&.Mui-focused fieldset': { borderColor: '#d6aa3a' },
    },
};

const TTL_OPTIONS = [
    { value: 1, label: '1 hora' },
    { value: 6, label: '6 horas' },
    { value: 24, label: '24 horas' },
    { value: 72, label: '3 días' },
    { value: 168, label: '7 días' },
];

const DeliveryForm = ({ onDelivered }) => {
    const { auth, token, validateUser } = useIndex();

    const [method, setMethod] = useState('document');
    const [submitting, setSubmitting] = useState(false);
    const [qrPayload, setQrPayload] = useState(null);

    const {
        register,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors },
    } = useForm({
        defaultValues: {
            moves: '',
            recipient_document: '',
            expires_in_hours: 24,
        },
    });

    const availableMoves = Number(auth?.moves ?? 0);
    const ttlValue = watch('expires_in_hours');

    const handleMethodChange = (_e, v) => {
        setMethod(v === 0 ? 'document' : 'qr');
    };

    const submit = async (form) => {
        const moves = Number(form.moves);
        if (!moves || moves <= 0) return;
        if (moves > availableMoves) return;

        const payload = { moves, method };
        if (method === 'document') {
            payload.recipient_document = String(form.recipient_document ?? '').trim();
        } else {
            payload.expires_in_hours = Number(form.expires_in_hours) || 24;
        }

        const confirmText = method === 'document'
            ? `¿Entregar ${moves} jugadas al jugador con documento ${payload.recipient_document}?`
            : `¿Generar un QR de ${moves} jugadas con vigencia de ${payload.expires_in_hours} horas?`;

        Confirm.show(
            'Confirmar entrega',
            confirmText,
            'Sí, entregar',
            'Cancelar',
            () => sendDelivery(payload),
            () => { },
        );
    };

    const sendDelivery = async (payload) => {
        setSubmitting(true);
        try {
            const { data } = await createDelivery(token, payload);
            if (data?.success) {
                if (payload.method === 'document') {
                    Report.success(
                        'Entrega exitosa',
                        data?.message ?? 'Jugadas entregadas correctamente.',
                        'Aceptar',
                        { textAlign: 'center' },
                    );
                } else {
                    setQrPayload(data?.data ?? null);
                }
                if (typeof validateUser === 'function') validateUser();
                reset({ moves: '', recipient_document: '', expires_in_hours: payload.expires_in_hours ?? 24 });
                onDelivered?.();
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <StyledCard>
                <CardContent sx={{ p: 2.5 }}>
                    <SummaryRow>
                        <Box>
                            <span className="label">Distribuidor</span>
                            <strong>{auth?.name} {auth?.lastname ?? ''}</strong>
                        </Box>
                        <Chip
                            label={`${availableMoves} jugadas disponibles`}
                            size="small"
                            sx={{
                                fontWeight: 900,
                                color: '#0f5c36',
                                background: 'rgba(46, 125, 50, 0.12)',
                                border: '1px solid rgba(46, 125, 50, 0.25)',
                            }}
                        />
                    </SummaryRow>

                    <MethodTabs value={method === 'document' ? 0 : 1} onChange={handleMethodChange}>
                        <Tab icon={<BadgeOutlinedIcon />} iconPosition="start" label="Por cédula" />
                        <Tab icon={<QrCode2OutlinedIcon />} iconPosition="start" label="Por QR" />
                    </MethodTabs>

                    <form onSubmit={handleSubmit(submit)} noValidate>
                        <FieldsGrid>
                            <TextField
                                size="small"
                                type="number"
                                label="Cantidad de jugadas"
                                inputProps={{ min: 1, max: availableMoves, step: 1 }}
                                onWheel={(e) => e.target.blur()}
                                onKeyDown={(e) => {
                                    if (['-', 'e', 'E', '+', '.', ','].includes(e.key)) {
                                        e.preventDefault();
                                    }
                                }}
                                error={!!errors.moves}
                                helperText={errors.moves?.message}
                                sx={fieldStyles}
                                {...register('moves', {
                                    required: 'Indica una cantidad.',
                                    min: { value: 1, message: 'Debe ser mayor a 0.' },
                                    max: { value: availableMoves, message: `No tienes más de ${availableMoves}.` },
                                    valueAsNumber: true,
                                })}
                            />

                            {method === 'document' ? (
                                <TextField
                                    size="small"
                                    label="Cédula del Jugador"
                                    placeholder="Ej. 1234567890"
                                    error={!!errors.recipient_document}
                                    helperText={errors.recipient_document?.message}
                                    sx={fieldStyles}
                                    {...register('recipient_document', {
                                        required: 'Indica la cédula del jugador.',
                                        minLength: { value: 4, message: 'Cédula muy corta.' },
                                    })}
                                />
                            ) : (
                                <FormControl size="small" sx={fieldStyles}>
                                    <InputLabel>Vigencia del QR</InputLabel>
                                    <Select
                                        label="Vigencia del QR"
                                        value={ttlValue ?? 24}
                                        onChange={(e) => setValue('expires_in_hours', Number(e.target.value))}
                                    >
                                        {TTL_OPTIONS.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        </FieldsGrid>

                        <LoadingButton
                            fullWidth
                            type="submit"
                            variant="contained"
                            loading={submitting}
                            disabled={availableMoves <= 0}
                            startIcon={method === 'document' ? <SendOutlinedIcon /> : <QrCode2OutlinedIcon />}
                            sx={primaryButtonStyles}
                        >
                            <span>{method === 'document' ? 'Entregar ahora' : 'Generar QR'}</span>
                        </LoadingButton>

                        {availableMoves <= 0 && (
                            <Typography className="empty-warning">
                                No tienes jugadas disponibles para entregar. Pídele a tu empresa que te asigne más.
                            </Typography>
                        )}
                    </form>
                </CardContent>
            </StyledCard>

            <DeliveryQrModal
                open={!!qrPayload}
                payload={qrPayload}
                onClose={() => setQrPayload(null)}
            />
        </>
    );
};

export default DeliveryForm;

const primaryButtonStyles = {
    mt: 2,
    borderRadius: '12px',
    fontWeight: 900,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    background: 'linear-gradient(90deg, #10285f 0%, #1a2f5c 100%)',
    boxShadow: '0 6px 14px rgba(15, 34, 77, 0.18)',
    '&:hover': {
        background: 'linear-gradient(90deg, #1a2f5c 0%, #10285f 100%)',
    },
};

const StyledCard = styled(Card)`
    border-radius: 18px !important;
    border: 1px solid rgba(15, 34, 77, 0.1);
    box-shadow: 0 8px 18px rgba(15, 34, 77, 0.07) !important;
    background: linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%) !important;

    .empty-warning {
        display: block;
        margin-top: 0.6rem;
        color: #b71c1c;
        font-size: 0.78rem;
        font-weight: 700;
        text-align: center;
    }
`;

const SummaryRow = styled(Box)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.6rem;
    padding: 0.65rem 0.85rem;
    border-radius: 14px;
    background: rgba(16, 40, 95, 0.045);
    border: 1px solid rgba(15, 34, 77, 0.08);
    margin-bottom: 1rem;

    .label {
        display: block;
        color: rgba(15, 34, 77, 0.6);
        font-size: 0.7rem;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.4px;
    }

    strong {
        color: #10285f;
        font-size: 0.95rem;
        font-weight: 900;
    }
`;

const MethodTabs = styled(Tabs)`
    margin-bottom: 1rem;
    min-height: 38px !important;

    .MuiTab-root {
        text-transform: none;
        font-weight: 800;
        font-size: 0.82rem;
        color: rgba(15, 34, 77, 0.62);
        min-height: 38px !important;
        padding: 0.4rem 0.9rem !important;

        &.Mui-selected { color: #10285f; }
    }

    .MuiTabs-indicator {
        background-color: #d6aa3a;
        height: 3px;
        border-radius: 3px 3px 0 0;
    }
`;

const FieldsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.6rem;
    margin-bottom: 0.6rem;

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;
